/**
 * AlertsContext — Estado global de alertas proactivas QuiPus.
 *
 * Coordina AlertsEngine (queries) con el estado React (dismissed, panel open/close).
 * Re-ejecuta el engine cuando cambia empresa, período o liveTransactions.
 *
 * Dismissed state: localStorage `quipus_dismissed_alerts_{firmId}`
 *   → Record<alertId, expiresAt (ms timestamp)>
 *   → Auto-expira en 24 horas.
 */

import {
  createContext, useContext, useState, useEffect,
  useCallback, type ReactNode,
} from 'react';
import { generateAlerts, type QuiPusAlert } from '../services/AlertsEngine';
import { useCompanyContext } from './CompanyContext';
import { useSyncContext } from './SyncContext';
import { useLang } from './LangContext';
import type { Transaction } from '../types';
import type { SerializedJournalEntry } from '../services/AccountingEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AlertsContextValue {
  /** All alerts (including dismissed). Use .dismissed to filter. */
  alerts: QuiPusAlert[];
  /** Count of undismissed critical + warning alerts (for bell badge). */
  alertCount: number;
  dismissAlert: (id: string) => void;
  dismissAll: () => void;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  loading: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AlertsContext = createContext<AlertsContextValue | null>(null);

export function useAlertsContext(): AlertsContextValue {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error('useAlertsContext must be used within <AlertsProvider>');
  return ctx;
}

// ─── Dismissed localStorage helpers ──────────────────────────────────────────

const DISMISSED_TTL = 24 * 60 * 60 * 1000; // 24 h

function dismissedKey(firmId: string): string {
  return `quipus_dismissed_alerts_${firmId}`;
}

function loadDismissed(firmId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(dismissedKey(firmId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    // Purge expired entries to keep localStorage lean
    const now = Date.now();
    return Object.fromEntries(Object.entries(parsed).filter(([, exp]) => exp > now));
  } catch { return {}; }
}

function saveDismissed(firmId: string, dismissed: Record<string, number>): void {
  try { localStorage.setItem(dismissedKey(firmId), JSON.stringify(dismissed)); }
  catch { /* storage full — best-effort */ }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AlertsProvider({ children }: { children: ReactNode }) {
  const { activeCompany, activeFirmId, activeCompanyId, filterPeriodo } = useCompanyContext();
  const { liveTransactions, liveJournalEntries } = useSyncContext();
  const { lang } = useLang();

  const [rawAlerts,  setRawAlerts]  = useState<QuiPusAlert[]>([]);
  const [dismissed,  setDismissed]  = useState<Record<string, number>>({});
  const [panelOpen,  setPanelOpen]  = useState(false);
  const [loading,    setLoading]    = useState(false);

  // Load dismissed map whenever firm changes
  useEffect(() => {
    if (activeFirmId) setDismissed(loadDismissed(activeFirmId));
  }, [activeFirmId]);

  // Run AlertsEngine when data changes
  useEffect(() => {
    if (!activeFirmId || !activeCompanyId || !activeCompany) return;
    let cancelled = false;
    setLoading(true);

    generateAlerts(
      activeFirmId,
      activeCompanyId,
      activeCompany,
      liveTransactions as Transaction[],
      liveJournalEntries as SerializedJournalEntry[],
      lang,
    )
      .then(alerts => { if (!cancelled) { setRawAlerts(alerts); setLoading(false); } })
      .catch(err  => { if (!cancelled) { console.warn('[AlertsContext]', err); setLoading(false); } });

    return () => { cancelled = true; };
  // filterPeriodo is intentionally in deps so alerts re-run on period switch
  }, [activeFirmId, activeCompanyId, activeCompany, liveTransactions, liveJournalEntries, filterPeriodo, lang]);

  // Merge dismissed timestamps into alert objects
  const now = Date.now();
  const alerts: QuiPusAlert[] = rawAlerts.map(a => ({
    ...a,
    dismissed: (dismissed[a.id] ?? 0) > now,
  }));

  const alertCount = alerts.filter(
    a => !a.dismissed && (a.severity === 'critical' || a.severity === 'warning'),
  ).length;

  const dismissAlert = useCallback((id: string) => {
    if (!activeFirmId) return;
    const next = { ...dismissed, [id]: Date.now() + DISMISSED_TTL };
    setDismissed(next);
    saveDismissed(activeFirmId, next);
  }, [dismissed, activeFirmId]);

  const dismissAll = useCallback(() => {
    if (!activeFirmId) return;
    const next = { ...dismissed };
    rawAlerts.forEach(a => { next[a.id] = Date.now() + DISMISSED_TTL; });
    setDismissed(next);
    saveDismissed(activeFirmId, next);
  }, [dismissed, rawAlerts, activeFirmId]);

  const openPanel  = useCallback(() => setPanelOpen(true),  []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  return (
    <AlertsContext.Provider value={{
      alerts, alertCount,
      dismissAlert, dismissAll,
      panelOpen, openPanel, closePanel,
      loading,
    }}>
      {children}
    </AlertsContext.Provider>
  );
}
